import { Box, Typography } from "@mui/material";
import { OrionInput } from "@orion/OrionComponents/OrionInput";
import { useForm } from "@tanstack/react-form";
import {
  Link,
  createFileRoute,
  redirect,
  useRouter,
} from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { enqueueSnackbar } from "notistack";
import { type FormEvent, useLayoutEffect, useState } from "react";
import { z } from "zod";

import type { Organization } from "@/types/auth";

import AuthLayout from "@/components/AuthLayout/AuthLayout";
import LoginFormSubmitButton from "@/components/LoginFormSubmitButton/LoginFormSubmitButton";
import { useAuth } from "@/contexts/AuthContext";
import {
  authLinkStyles,
  labelStyles,
  sxTextFieldStyles,
} from "@/routes/-authStyles";
import { enqueueErrorSnackbar } from "@/utils/snackbarHelpers";

const resetPasswordSearchSchema = z.object({
  token: z.string().optional(),
});

export const Route = createFileRoute("/reset-password")({
  validateSearch: zodValidator(resetPasswordSearchSchema),
  beforeLoad: ({ context, search }) => {
    if (context.auth?.isAuthenticated && !search.token) {
      throw redirect({ to: "/" });
    }
  },
  component: ResetPasswordComponent,
});

const resetPasswordSchema = z.object({
  password: z.string().min(8, "Password must be at least 8 characters"),
});

function ResetPasswordComponent() {
  const auth = useAuth();
  const router = useRouter();
  const search = Route.useSearch();
  const [orgSelection, setOrgSelection] = useState<{
    pendingAuthenticationToken: string;
    organizations: Organization[];
  } | null>(null);

  useLayoutEffect(() => {
    if (auth.isAuthenticated && !search.token) {
      void router.navigate({ to: "/" });
    }
  }, [auth.isAuthenticated, search.token, router]);

  const form = useForm({
    defaultValues: {
      password: "",
    },
    validators: {
      onSubmit: resetPasswordSchema,
    },
    onSubmit: async ({ value }) => {
      if (!search.token) return;
      try {
        const result = await auth.confirmPasswordReset(
          search.token,
          value.password,
        );
        if (result.kind === "organization_selection_required") {
          setOrgSelection({
            pendingAuthenticationToken: result.pendingAuthenticationToken,
            organizations: result.organizations,
          });
          return;
        }
        if (result.kind === "password_reset_success") {
          enqueueSnackbar("Password reset successful. Please log in.", {
            variant: "success",
          });
          await router.navigate({ to: "/login" });
          return;
        }
        await router.navigate({ to: "/" });
      } catch (error: unknown) {
        enqueueErrorSnackbar(error, "Password reset failed");
      }
    },
  });

  const handleSelectOrganization = async (organizationId: string) => {
    if (!orgSelection) return;
    try {
      await auth.selectOrganization(
        organizationId,
        orgSelection.pendingAuthenticationToken,
      );
      await router.navigate({ to: "/" });
    } catch (error: unknown) {
      enqueueErrorSnackbar(error, "Organization selection failed");
    }
  };

  const hasToken = !!search.token;

  const getTitle = () => {
    if (orgSelection) return "Select Organization";
    if (hasToken) return "Set a new password";
    return "Invalid reset link";
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    void form.handleSubmit();
  };

  const showForm = hasToken && !orgSelection;

  return (
    <AuthLayout
      title={getTitle()}
      onSubmit={showForm ? handleSubmit : undefined}
    >
      {orgSelection ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 3,
          }}
        >
          {orgSelection.organizations.map((org) => (
            <LoginFormSubmitButton
              key={org.id}
              type="button"
              label={org.name}
              onClick={() => {
                void handleSelectOrganization(org.id);
              }}
              disabled={auth.isLoading}
            />
          ))}
        </Box>
      ) : hasToken ? (
        <>
          <form.Field name="password">
            {(field) => (
              <OrionInput
                field={field}
                label="New password"
                type="password"
                placeholder="Enter your new password"
                sx={sxTextFieldStyles}
                labelStyles={labelStyles}
              />
            )}
          </form.Field>

          <LoginFormSubmitButton
            type="submit"
            label="Reset password"
            disabled={form.state.isSubmitting}
          />
        </>
      ) : (
        <>
          <Typography
            sx={{
              color: "#fff",
              fontSize: "18px",
              fontWeight: 300,
              textAlign: "center",
            }}
          >
            This reset link is invalid or has expired.
          </Typography>
          <Box component={Link} to="/login" sx={authLinkStyles}>
            Back to login
          </Box>
        </>
      )}
    </AuthLayout>
  );
}
